/**
 * KnowledgeBaseAdmin — Back-office d'édition des 15 fiches de la base de connaissances.
 *
 * Accessible depuis Settings > Base de connaissances (admin_full uniquement).
 * Permet de :
 *   - Voir toutes les entrées en tableau
 *   - Éditer titre / définition / où trouver / catégorie / notions liées
 *   - Uploader une vraie capture d'écran (remplace le SVG placeholder)
 *   - Supprimer l'image custom pour revenir au placeholder
 */
import { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BookOpen, Pencil, Upload, Trash2, Image as ImageIcon, Check, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const CATEGORY_LABEL = {
    reglementaire: { label: 'Réglementaire', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
    metrique:      { label: 'Métrique',      cls: 'bg-purple-100 text-purple-800 border-purple-200' },
    terrain:       { label: 'Terrain',       cls: 'bg-amber-100 text-amber-800 border-amber-200' },
};

export default function KnowledgeBaseAdmin() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);   // entrée en édition

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/knowledge-base');
            setEntries((data?.entries || []).sort((a, b) => (a.title || '').localeCompare(b.title || '')));
        } catch (e) {
            toast.error('Erreur chargement base de connaissances');
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-4" data-testid="kb-admin-root">
            <Card>
                <CardHeader>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-sm">
                            <BookOpen className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <CardTitle className="text-lg">Base de connaissances énergétique</CardTitle>
                            <CardDescription>Contenu affiché dans les tooltips « Où trouver l&apos;information ? » de la Carte d&apos;identité énergétique</CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-sm text-slate-500">Chargement des fiches…</p>
                    ) : (
                        <div className="divide-y divide-slate-200 rounded-lg border border-slate-200 overflow-hidden">
                            {entries.map((e) => (
                                <div
                                    key={e.key}
                                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50"
                                    data-testid={`kb-row-${e.key}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-semibold text-slate-800 text-sm">{e.title}</span>
                                            {e.category && (
                                                <Badge className={`text-[10px] border ${CATEGORY_LABEL[e.category]?.cls || ''}`}>
                                                    {CATEGORY_LABEL[e.category]?.label || e.category}
                                                </Badge>
                                            )}
                                            {e.custom_image_ext ? (
                                                <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">
                                                    <ImageIcon className="w-2.5 h-2.5 mr-0.5" /> Image custom
                                                </Badge>
                                            ) : (
                                                <Badge className="text-[10px] bg-slate-100 text-slate-500 border-slate-200">
                                                    <ImageIcon className="w-2.5 h-2.5 mr-0.5" /> Placeholder SVG
                                                </Badge>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">{e.definition}</p>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setEditing(e)}
                                        data-testid={`kb-edit-${e.key}`}
                                    >
                                        <Pencil className="w-3.5 h-3.5 mr-1" /> Éditer
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <EditKbDialog
                entry={editing}
                onClose={() => setEditing(null)}
                onSaved={() => { setEditing(null); load(); }}
            />
        </div>
    );
}

function EditKbDialog({ entry, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [imageBust, setImageBust] = useState(Date.now());

    useEffect(() => {
        if (entry) {
            setForm({
                title: entry.title || '',
                definition: entry.definition || '',
                where_to_find: entry.where_to_find || '',
                category: entry.category || '',
                related_keys: (entry.related_keys || []).join(', '),
            });
            setImageBust(Date.now());
        }
    }, [entry]);

    if (!entry) return null;

    const save = async () => {
        setSaving(true);
        try {
            await api.put(`/knowledge-base/${entry.key}`, {
                key: entry.key,
                title: form.title,
                definition: form.definition,
                where_to_find: form.where_to_find,
                category: form.category || null,
                related_keys: form.related_keys
                    ? form.related_keys.split(',').map((s) => s.trim()).filter(Boolean)
                    : [],
            });
            toast.success('Fiche enregistrée');
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur enregistrement');
        } finally {
            setSaving(false);
        }
    };

    const uploadImage = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Image trop lourde (5 Mo max)'); return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await api.post(`/knowledge-base/${entry.key}/image`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.success('Image uploadée');
            setImageBust(Date.now());
            onSaved?.();  // refresh list to show "Image custom" badge
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur upload');
        } finally {
            setUploading(false);
        }
    };

    const deleteImage = async () => {
        if (!window.confirm('Supprimer l\'image custom et revenir au placeholder SVG ?')) return;
        try {
            await api.delete(`/knowledge-base/${entry.key}/image`);
            toast.success('Image supprimée');
            setImageBust(Date.now());
            onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur suppression');
        }
    };

    const imgSrc = `${process.env.REACT_APP_BACKEND_URL}/api/knowledge-base/${entry.key}/image?_=${imageBust}`;

    return (
        <Dialog open={!!entry} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="kb-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Pencil className="w-4 h-4 text-purple-600" />
                        Éditer la fiche <code className="text-xs px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200">{entry.key}</code>
                    </DialogTitle>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Colonne gauche : contenu texte */}
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs">Titre</Label>
                            <Input
                                value={form.title || ''}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                data-testid="kb-form-title"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Catégorie</Label>
                            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                                <SelectTrigger data-testid="kb-form-category"><SelectValue placeholder="—" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="reglementaire">Réglementaire</SelectItem>
                                    <SelectItem value="metrique">Métrique</SelectItem>
                                    <SelectItem value="terrain">Terrain</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Définition</Label>
                            <Textarea
                                rows={5}
                                value={form.definition || ''}
                                onChange={(e) => setForm({ ...form, definition: e.target.value })}
                                data-testid="kb-form-definition"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Où trouver l&apos;information ?</Label>
                            <Textarea
                                rows={4}
                                value={form.where_to_find || ''}
                                onChange={(e) => setForm({ ...form, where_to_find: e.target.value })}
                                data-testid="kb-form-where"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Notions liées (clés séparées par virgule)</Label>
                            <Input
                                value={form.related_keys || ''}
                                onChange={(e) => setForm({ ...form, related_keys: e.target.value })}
                                placeholder="cref, operat, sub"
                                data-testid="kb-form-related"
                            />
                        </div>
                    </div>

                    {/* Colonne droite : image */}
                    <div className="space-y-2">
                        <Label className="text-xs">Image explicative</Label>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                            <img
                                key={imageBust}
                                src={imgSrc}
                                alt={entry.title}
                                className="w-full h-auto block"
                                data-testid="kb-form-current-image"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="kb-form-upload-btn"
                            >
                                <Upload className={`w-3.5 h-3.5 mr-1 ${uploading ? 'animate-pulse' : ''}`} />
                                {uploading ? 'Upload…' : 'Uploader une image'}
                            </Button>
                            {entry.custom_image_ext && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={deleteImage}
                                    className="text-rose-600 hover:text-rose-700"
                                    data-testid="kb-form-delete-image-btn"
                                >
                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Retour placeholder
                                </Button>
                            )}
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            className="hidden"
                            onChange={(e) => e.target.files?.[0] && uploadImage(e.target.files[0])}
                        />
                        <p className="text-[10px] text-slate-400">PNG, JPG, WEBP ou SVG · 5 Mo max · remplacera le SVG placeholder généré automatiquement.</p>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button
                        onClick={save}
                        disabled={saving}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="kb-form-save-btn"
                    >
                        <Check className="w-4 h-4 mr-1" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
